import { Injectable } from '@angular/core';

import { ApiPayloadConfig } from '../models/diagnostic-profile';
import { DiagnosticProfile } from '../models/diagnostic-profile';

/**
 * Hard upper bound for the body-cap settings UI. Values are clamped to
 * [0, DEEP_DEBUG_BODY_MAX] on write and read.
 *
 * Size budget note: the static deepDebug caps (8192 byte each) are covered
 * by the pre-deploy regression guard in diagnostic-profiles.spec.ts (< 10 MB
 * worst-case). The runtime override can raise the body cap to 32768 and
 * maxEvents to 2000, which can exceed both the 10 MB internal budget and the
 * 25 MB email attachment limit. This is an accepted trade-off for a
 * DIAGNOSTIC_DEEP_DEBUG role-gated tool: the UI SIZE_WARNING/EVENTS_WARNING
 * surfaces the risk, and bundles above the email limit must be transferred
 * manually (e.g. shared drive, ticket upload). The regression guard in
 * diagnostic-profiles.spec.ts explicitly covers only the static profile caps,
 * not runtime overrides.
 */
export const DEEP_DEBUG_BODY_MAX = 32768;
export const DEEP_DEBUG_BODY_WARN = 16384;

export const DEEP_DEBUG_EVENTS_MIN = 100;
export const DEEP_DEBUG_EVENTS_MAX = 2000;
export const DEEP_DEBUG_EVENTS_WARN = 1000;

const STORAGE_KEY = 'easylot.diagnostic.deepDebugConfig';

/**
 * Combined config stored under a single localStorage key (EAS-2047 body/header
 * caps + EAS-2048 maxEvents). Extends ApiPayloadConfig so existing body-cap
 * callers (resolveApiPayload) remain unaffected.
 */
export interface DeepDebugConfig extends ApiPayloadConfig {
  maxEvents?: number;
}

function clampBodyCap(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  const n = Number(value);
  if (!isFinite(n)) return undefined;
  return Math.max(0, Math.min(DEEP_DEBUG_BODY_MAX, Math.round(n)));
}

function clampEventCount(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  const n = Number(value);
  if (!isFinite(n)) return undefined;
  return Math.max(
    DEEP_DEBUG_EVENTS_MIN,
    Math.min(DEEP_DEBUG_EVENTS_MAX, Math.round(n))
  );
}

/**
 * Stores and retrieves a runtime override for the deepDebug profile's
 * apiPayload config and maxEvents. All other profiles use their static values
 * from DIAGNOSTIC_PROFILES unchanged.
 *
 * localStorage is best-effort: read/write failures are silently swallowed
 * (private browsing, quota exceeded, disabled storage) and the static
 * defaults take effect instead.
 *
 * Caching: the parsed config is kept in memory after the first read and
 * invalidated on write() and clear(). This avoids repeated localStorage.getItem
 * + JSON.parse on the push() hot path (DiagnosticBufferService calls
 * resolveMaxEvents on every event).
 *
 * Defense-in-depth note: the override only activates when the deepDebug
 * profile is the active profile, and deepDebug activation is role-gated in
 * the user-menu (availableProfiles). A mid-session role revocation without
 * logout leaves deepDebug potentially active (pre-existing behavior in
 * DiagnosticBufferService preference-restore); this service amplifies that
 * exposure by also applying the override. Mitigated by logout triggering a
 * buffer clear + profile reset (see DiagnosticBufferService logout handler).
 */
@Injectable({ providedIn: 'root' })
export class DiagnosticDeepDebugConfigService {
  // undefined = not yet loaded; null = loaded, nothing stored.
  private cache: DeepDebugConfig | null | undefined = undefined;

  read(): DeepDebugConfig | null {
    if (this.cache !== undefined) {
      return this.cache;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        this.cache = null;
        return null;
      }
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const config: DeepDebugConfig = {};
      const req = clampBodyCap(parsed['requestBodyMaxChars']);
      const res = clampBodyCap(parsed['responseBodyMaxChars']);
      if (req !== undefined) config.requestBodyMaxChars = req;
      if (res !== undefined) config.responseBodyMaxChars = res;
      if ('captureHeaders' in parsed) {
        config.captureHeaders = Boolean(parsed['captureHeaders']);
      }
      const ev = clampEventCount(parsed['maxEvents']);
      if (ev !== undefined) config.maxEvents = ev;
      this.cache = config;
      return config;
    } catch {
      this.cache = null;
      return null;
    }
  }

  write(config: DeepDebugConfig): void {
    try {
      const safe: DeepDebugConfig = {};
      const req = clampBodyCap(config.requestBodyMaxChars);
      const res = clampBodyCap(config.responseBodyMaxChars);
      if (req !== undefined) safe.requestBodyMaxChars = req;
      if (res !== undefined) safe.responseBodyMaxChars = res;
      if (config.captureHeaders !== undefined) {
        safe.captureHeaders = Boolean(config.captureHeaders);
      }
      const ev = clampEventCount(config.maxEvents);
      if (ev !== undefined) safe.maxEvents = ev;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
      this.cache = safe;
    } catch {
      // localStorage may be unavailable (private browsing, quota, disabled).
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Same silent fallback as write().
    } finally {
      this.cache = null;
    }
  }

  /**
   * Returns the effective apiPayload config for a given profile.
   *
   * For the deepDebug profile the stored runtime override takes precedence
   * over the static value; for all other profiles the static value is used.
   *
   * Replace semantics: the stored override is treated as a complete
   * replacement of the static apiPayload, not a merge. A field absent from
   * the stored config (e.g. responseBodyMaxChars not set) is absent from the
   * effective config — the static default does NOT serve as a fallback for
   * individual fields. The settings dialog seeds from the current effective
   * config, so a partial override can only arise from direct localStorage
   * manipulation outside the UI.
   *
   * Two-mode fallback: if the stored config contains only maxEvents (no api
   * fields), the static profile.apiPayload is returned intact. This preserves
   * the static body/header caps when the user has only adjusted maxEvents and
   * left the api fields at defaults (which the dialog never writes unless they
   * differ from defaults).
   */
  resolveApiPayload(profile: DiagnosticProfile): ApiPayloadConfig {
    if (profile.name === 'deepDebug') {
      const stored = this.read();
      if (stored) {
        // Extract only the ApiPayloadConfig fields, excluding maxEvents.
        const { maxEvents: _ignored, ...apiPayload } = stored;
        return Object.keys(apiPayload).length > 0
          ? apiPayload
          : profile.apiPayload ?? {};
      }
      return profile.apiPayload ?? {};
    }
    return profile.apiPayload ?? {};
  }

  /**
   * Returns the effective maxEvents limit for a given profile.
   * For deepDebug, the stored runtime override takes precedence over the
   * static profile value. For all other profiles the static value is used.
   * Uses the in-memory cache to avoid localStorage I/O on the push() hot path.
   */
  resolveMaxEvents(profile: DiagnosticProfile): number {
    if (profile.name === 'deepDebug') {
      return this.read()?.maxEvents ?? profile.maxEvents;
    }
    return profile.maxEvents;
  }
}
